/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Dimension } from '@opengeoweb/webmap';
import type { OpenLayersFeatureLayerProps } from './OpenLayersFeatureLayer';
import { OpenLayersFeatureLayer } from './OpenLayersFeatureLayer';
import {
  makeFeatureStyleLoading,
  makeFeatureStyleMultiParamLoading,
  useEDRGetParameterData,
  useEDRWMLayer,
} from '../../../edr-layer-api';
import { getDimensionParamsForWMS } from '../utils/makeDimValuesToPrefetch';
import { ShowLayerInfo } from '../utils/ShowLayerInfo';
import type { OnInitializeLayerProps } from '../utils/types';

interface TimeAwareEDRLocationLayerProps extends OpenLayersFeatureLayerProps {
  layerId?: string;
  edrBaseUrl: string;
  parameter: string;
  dimensions?: Dimension[];
  onInitializeLayer?: (payload: OnInitializeLayerProps) => void;
  opacity?: number;
  visible?: boolean;
  debugMode?: boolean;
}

const makeEDRLayerInfoTitle = (
  edrBaseUrl: string,
  parameter: string,
): string => {
  const [serviceUrl, collectionId] = edrBaseUrl.split('/collections/');
  return `${parameter} | ${collectionId} | ${serviceUrl}`;
};

export const TimeAwareEDRLocationLayer: React.FC<
  TimeAwareEDRLocationLayerProps
> = ({
  edrBaseUrl,
  parameter,
  style,
  zIndex,
  opacity,
  dimensions = [],
  layerId,
  visible = true,
  onInitializeLayer,
  debugMode,
}: TimeAwareEDRLocationLayerProps) => {
  // get a wmLayer instance
  const wmLayer = useEDRWMLayer(
    edrBaseUrl,
    parameter,
    layerId,
    onInitializeLayer,
  );

  // dimensionsValues is a key/value dictionary with dimension names specifically for WMS GetMap requests. We re-use it here for EDR.
  const dimensionsValues = getDimensionParamsForWMS(dimensions, wmLayer);

  const timeValue = dimensionsValues?.['TIME'];

  const edrQueryDateTime =
    wmLayer?.getDimension('time')?.getClosestValue(timeValue!) ?? timeValue;

  const { data: featurePoints, isFetching } = useEDRGetParameterData(
    edrBaseUrl,
    parameter,
    edrQueryDateTime,
  );

  const loadingStyle = parameter?.includes(',')
    ? makeFeatureStyleMultiParamLoading
    : makeFeatureStyleLoading;

  // Send empty collection if no API result yet
  const featureCollection = featurePoints ?? {
    features: [],
    type: 'FeatureCollection',
  };

  // Don't show anything if no parameter is selected
  const isVisible = visible && parameter.length > 0;

  return (
    <>
      <OpenLayersFeatureLayer
        declutter
        featureCollection={featureCollection}
        opacity={opacity}
        style={isFetching ? loadingStyle : style}
        visible={isVisible}
        zIndex={zIndex}
      />
      {!!debugMode && (
        <ShowLayerInfo
          dimensions={wmLayer?.dimensions ?? []}
          title={makeEDRLayerInfoTitle(edrBaseUrl, parameter)}
        />
      )}
    </>
  );
};
